<template>
  <div class="flex flex-column gap-2">
    <DragAndDropFile
      class="drag-and-drop"
      :class="{
        'has-files': fileUploaded,
      }"
      ref="dragAndDropRef"
      :allowed-extensions="`.${allowedExtension}`"
      @addFile="handleAddedFile()"
    />

    <Button
      v-if="!fileUploaded"
      icon="pi pi-file"
      class="w-full"
      label="Elegir fichero"
      @click="triggerGetFile()"
      :loading="loading"
      severity="secondary"
    />

    <template v-if="fileUploaded">
      <div class="w-full flex flex-column gap-1 mt-2">
        <label for="username" class="text-sm"
          >Introducir nombre de la versión</label
        >
        <InputText
          id="username"
          v-model="newPbixFileName"
          placeholder="Introducir nombre"
          @keydown.enter="handleAddPbix()"
        />
      </div>

      <Button
        icon="pi pi-upload"
        label="Subir versión"
        @click="handleAddPbix()"
        :disabled="!newPbixFileName"
        :loading="loading"
      />

      <small v-if="dragAndDropErrorRef" class="text-danger">
        {{ dragAndDropErrorRef }}
      </small>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { DragAndDropFile } from "@sttedra_ui/framework";

// #region Props, Emits
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  lastVersion: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["addPbix"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const newPbixFileName = ref(null);
const dragAndDropRef = ref();
const dragAndDropErrorRef = ref("");
const allowedExtension = "pbix";

const fileUploaded = computed(() => {
  return dragAndDropRef.value
    ? dragAndDropRef.value.getFiles().length > 0
    : false;
});
// #endregion

// #region Functions
function triggerGetFile() {
  dragAndDropErrorRef.value = "";

  const comp = dragAndDropRef.value;
  if (!comp) return;

  const root = comp.$el ?? comp;
  const input = root?.querySelector?.('input[type="file"]');

  if (input) {
    input.click();
    return;
  }
}

function handleAddPbix() {
  emit("addPbix");
}

function handleAddedFile() {
  const files = dragAndDropRef.value.getFiles();
  dragAndDropRef.value.clear();

  dragAndDropRef.value.setFiles([files[files.length - 1]]);

  const file = getPbixFile();
  const fileName = file?.name;

  if (fileName) {
    newPbixFileName.value = fileName.split(".").slice(0, -1).join(".");

    if (props.lastVersion) {
      newPbixFileName.value += ` (v${props.lastVersion.numVersion + 1})`;
    }
  }
}

function getPbixFile() {
  const files = dragAndDropRef.value.getFiles();
  return files?.length > 0 ? files[0] : null;
}

function getPbixFileName() {
  return newPbixFileName.value;
}

function validatePbix() {
  let validate = true;
  const files = dragAndDropRef.value.getFiles();

  if (files.length == 0) {
    dragAndDropErrorRef.value = "Debe seleccionar un archivo";
    validate = false;
  } else if (files.length > 1) {
    dragAndDropErrorRef.value = "Solo se permite un archivo";
    validate = false;
  } else if (
    files.length > 0 &&
    files[0].name.split(".").pop() != allowedExtension
  ) {
    dragAndDropErrorRef.value = `El archivo debe ser de tipo .${allowedExtension}`;
    validate = false;
  } else {
    dragAndDropErrorRef.value = "";
  }

  return validate;
}

function clearPbix() {
  newPbixFileName.value = null;
  dragAndDropRef.value.clear();
  dragAndDropErrorRef.value = "";
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  getPbixFile,
  getPbixFileName,
  clearPbix,
  validatePbix,
  dragAndDropErrorRef,
});
// #endregion
</script>

<style lang="scss" scoped>
.drag-and-drop {
  &.has-files {
    :deep(.container-drop__image) {
      display: none;
    }

    :deep(.container-drop__files__file) {
      margin-top: 0;
    }
  }
}
</style>
